// src/components/markdown-renderer/components/SectionRenderer.tsx
import React, { useRef, useEffect } from 'react'
import DOMPurify from 'dompurify'
import classNames from 'classnames'
import { Shield, Loader2, HelpCircle } from 'lucide-react'
import type MarkdownIt from 'markdown-it'
import SectionReviewPanel from '../../sections/SectionReviewPanel'
import styles from '~/styles/MarkdownRenderer.module.css'
import type { SectionReviews, SectionMeta } from '../types'

interface SectionRendererProps {
  sectionId: string
  sectionTitle: string
  sectionContent: string
  sectionState: 'none' | 'accepted' | 'rejected' | 'pending'
  sectionReview?: SectionReviews[string]
  isProcessing: boolean
  isProtected: boolean
  sectionMeta?: SectionMeta[string]
  sectionExplanation?: string
  isExplanationMinimized?: boolean
  selectedSection: string | null
  onSectionClick: (sectionId: string, sectionName: string, event: React.MouseEvent) => void
  onAcceptReview?: (sectionId: string) => void
  onRejectReview?: (sectionId: string) => void
  onToggleExplanation?: (sectionId: string) => void
  formatDate: (timestamp?: number) => string
  markdownInstance: MarkdownIt // Add markdown instance prop
}

export const SectionRenderer: React.FC<SectionRendererProps> = ({
  sectionId,
  sectionTitle,
  sectionContent,
  sectionState,
  sectionReview,
  isProcessing,
  isProtected,
  sectionMeta,
  sectionExplanation,
  isExplanationMinimized,
  selectedSection,
  onSectionClick,
  onAcceptReview,
  onRejectReview,
  onToggleExplanation,
  formatDate,
  markdownInstance
}) => {
  const normalizedSectionTitle = sectionTitle || 'Unnamed Section'
  const hasReview = !!sectionReview
  const hasExplanation = !!sectionExplanation
  const sectionContentRef = useRef<HTMLDivElement>(null)
  const explanationContentRef = useRef<HTMLDivElement>(null)

  // Trigger mermaid rendering after content is inserted
  const triggerMermaidRendering = (container: HTMLElement) => {
    const mermaidElements = container.querySelectorAll('.mermaid-diagram[data-render-state="pending"]')
    if (mermaidElements.length > 0) {
      console.log(`Found ${mermaidElements.length} pending mermaid diagrams in section ${sectionId}`)
      
      // Trigger a custom event to notify the mermaid renderer
      const event = new CustomEvent('mermaidContentAdded', {
        detail: { 
          sectionId,
          elements: Array.from(mermaidElements)
        }
      })
      document.dispatchEvent(event)
    }
  }

  // Effect to handle mermaid rendering after content changes
  useEffect(() => {
    if (sectionContentRef.current) {
      triggerMermaidRendering(sectionContentRef.current)
    }
  }, [sectionContent, sectionId])

  useEffect(() => {
    if (explanationContentRef.current && sectionExplanation && !isExplanationMinimized) {
      triggerMermaidRendering(explanationContentRef.current)
    }
  }, [sectionExplanation, isExplanationMinimized, sectionId])

  return (
    <div
      data-section-id={sectionId}
      data-section-title={normalizedSectionTitle}
      className={classNames(styles.section, {
        [styles['section-selected']]: selectedSection === sectionId,
        [styles['section-hoverable']]: true,
        [styles[`section-state-${sectionState.toLowerCase()}`]]: true,
        [styles['section-with-review']]: hasReview,
        [styles['section-protected']]: isProtected,
        [styles['section-processing']]: isProcessing,
      })}
      role="button"
      tabIndex={0}
      onClick={(event) => onSectionClick(sectionId, normalizedSectionTitle, event)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          onSectionClick(sectionId, normalizedSectionTitle, e as any)
        }
      }}
    >
      {/* Section title with icons */}
      <div className={styles.sectionTitle}>
        <span>{normalizedSectionTitle}</span>
        <div className={styles.sectionIcons}>
          {isProtected && (
            <Shield
              size={14}
              className="text-blue-500"
            />
          )}
          {hasExplanation && (
            <HelpCircle
              size={14}
              className="text-green-500"
            />
          )}
          {isProcessing && (
            <Loader2
              size={14}
              className="text-orange-500 animate-spin"
            />
          )}
        </div>
        {/* Display update info if available */}
        {sectionMeta?.updatedAt && (
          <span className={styles.sectionUpdateInfo}>
            Updated {sectionMeta?.updatedBy || ''} {formatDate(sectionMeta?.updatedAt)}
          </span>
        )}
      </div>      {/* Section content */}
      <div
        ref={sectionContentRef}
        className={styles['section-content']}
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(markdownInstance.render(sectionContent)),
        }}
      />

      {/* Render explanation panel if available */}
      {hasExplanation && (
        <div className={styles['section-explanation-container']}>
          <div className={styles['section-explanation']}>
            <h4 
              className={classNames(styles['section-explanation-header'])}
              onClick={(e) => {
                e.stopPropagation()
                onToggleExplanation?.(sectionId)
              }}
              title="Click to toggle explanation visibility"
            >
              <HelpCircle className="inline mr-1" size={16} />
              Explanation
              {isExplanationMinimized ? (
                <span className={styles['section-explanation-toggle']}>(minimized)</span>
              ) : (
                <span className={styles['section-explanation-toggle']}>(click to minimize)</span>
              )}
            </h4>            {/* Only show explanation content if not minimized */}
            {!isExplanationMinimized && (
              <div
                ref={explanationContentRef}
                className={styles['section-explanation-content']}
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(markdownInstance.render(sectionExplanation)),
                }}
              />
            )}
          </div>
        </div>
      )}

      {/* Render review panel inside section when a review exists */}
      {hasReview && (
        <div
          className={styles['section-review-container']}
          onClick={(e) => e.stopPropagation()} // Prevent clicks in review panel from selecting section
        >
          <SectionReviewPanel
            sectionId={sectionId}
            originalContent={sectionReview.originalContent}
            suggestedContent={sectionReview.suggestedContent}
            commandType={sectionReview.commandType}
            onAccept={() => {
              console.log(`Accept button clicked for section ${sectionId}`)
              if (onAcceptReview) onAcceptReview(sectionId)
            }}
            onReject={() => {
              console.log(`Reject button clicked for section ${sectionId}`)
              if (onRejectReview) onRejectReview(sectionId)
            }}
          />
        </div>
      )}
    </div>
  )
}
